import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../api";
import { Download, Shield, CheckCircle2, AlertTriangle, XCircle, Building2, Calendar, Users, MapPin } from "lucide-react";
import useNoIndex from "../useNoIndex";

export default function BuyerPortal() {
  useNoIndex();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/buyer/${token}`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Invitation unavailable.");
      }
    })();
  }, [token]);

  async function download(d) {
    const r = await axios.get(`${API_BASE}/buyer/${token}/document/${d.document_id}`);
    const link = document.createElement("a");
    link.href = `data:${r.data.mime_type};base64,${r.data.file_b64}`;
    link.download = r.data.file_name || d.name;
    link.click();
  }

  if (err) {
    return (
      <div className="container-cd py-24" data-testid="buyer-portal-error">
        <div className="overline" style={{ color: "var(--red)" }}>ACCESS ERROR</div>
        <h1 className="font-display text-5xl mt-4">{err}</h1>
      </div>
    );
  }
  if (!data) return <div className="container-cd py-20 overline">LOADING…</div>;

  const { invite, manufacturer, compliance, documents } = data;
  const noDocs = compliance.state === "no_documents";
  const scoreColor = noDocs ? "var(--ink-2)" : compliance.score >= 80 ? "var(--green)" : compliance.score >= 50 ? "var(--yellow)" : "var(--red)";

  return (
    <div data-testid="buyer-portal-page">
      {/* Header */}
      <section style={{ background: "var(--ink)", color: "white", borderBottom: "1px solid var(--line)" }}>
        <div className="container-cd py-12">
          <div className="overline" style={{ color: "var(--orange)" }}>
            <Shield size={11} className="inline mr-2 -mt-1" /> CROSS-BORDER DATA BRIDGE · CONFIDENTIAL
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="font-display text-4xl md:text-6xl leading-[0.92]">
                {manufacturer.company_name}
              </h1>
              <div className="mt-3 font-mono text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                LIVE COMPLIANCE DASHBOARD · FOR {invite.buyer_company.toUpperCase()}
              </div>
            </div>
            <div className="text-right">
              <div className="overline" style={{ color: "rgba(255,255,255,0.5)" }}>VIEWING AS</div>
              <div className="font-heading text-lg mt-1">{invite.buyer_name}</div>
              <div className="font-mono text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                {invite.buyer_email}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance score + stats */}
      <section className="container-cd py-12">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-4 cd-card p-8">
            <div className="overline">COMPLIANCE SCORE</div>
            {noDocs ? (
              <>
                <div className="mt-4 font-display text-5xl" style={{ color: "var(--ink-2)" }} data-testid="compliance-score">—</div>
                <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  No documents uploaded yet. Score will appear once the manufacturer uploads compliance files.
                </div>
              </>
            ) : (
              <>
                <div className="mt-4 font-display text-7xl" style={{ color: scoreColor }} data-testid="compliance-score">
                  {compliance.score}%
                </div>
                <div className="mt-4 h-3" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
                  <div style={{ width: `${compliance.score}%`, height: "100%", background: scoreColor }} />
                </div>
                <div className="mt-6 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {compliance.valid} valid · {compliance.expiring} expiring · {compliance.expired} expired
                </div>
              </>
            )}
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-0" style={{ border: "1px solid var(--line)" }}>
            <Stat icon={CheckCircle2} value={compliance.valid} label="Documents valid" color="var(--green)" />
            <Stat icon={AlertTriangle} value={compliance.expiring} label="Expiring < 60 days" color="var(--yellow)" />
            <Stat icon={XCircle} value={compliance.expired} label="Expired" color="var(--red)" last />
            <Stat icon={Building2} value={manufacturer.employees || "—"} label="Employees" />
            <Stat icon={Calendar} value={manufacturer.year_founded || "—"} label="Founded" />
            <Stat icon={MapPin} value={`${manufacturer.city || "—"}, ${manufacturer.state || ""}`} label="Plant location" last small />
          </div>
        </div>

        {/* Verified badge */}
        {manufacturer.verified && (
          <div className="mt-8 cd-card p-4 inline-flex items-center gap-3" style={{ borderColor: "var(--green)" }}>
            <Shield size={18} style={{ color: "var(--green)" }} />
            <div>
              <div className="font-heading font-semibold text-sm">Corridor Verified ✓</div>
              <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                IMMEX permit, USMCA cert, and identity verified by Corridor Digital
              </div>
            </div>
          </div>
        )}

        {/* Note from manufacturer */}
        {invite.note && (
          <div className="mt-8 cd-card p-6">
            <div className="overline">NOTE FROM {manufacturer.company_name.toUpperCase()}</div>
            <p className="mt-3 text-base">{invite.note}</p>
          </div>
        )}

        {/* Capabilities + Certifications */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <Bucket title="Capabilities" items={manufacturer.capabilities} />
          <Bucket title="Certifications" items={manufacturer.certifications} />
        </div>
      </section>

      {/* Documents */}
      <section className="container-cd pb-16">
        <div className="overline">COMPLIANCE DOCUMENTS · {documents.length}</div>
        {documents.length === 0 ? (
          <div className="cd-card p-12 mt-6 text-center font-mono text-sm">
            No documents uploaded yet. {manufacturer.company_name} will share them here as they're available.
          </div>
        ) : (
          <div className="cd-card p-0 mt-6 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Document</th>
                  <th>Category</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.document_id} data-testid={`buyer-doc-${d.document_id}`}>
                    <td>
                      <StatusDot status={d.status} />
                    </td>
                    <td>
                      <div className="font-semibold">{d.name}</div>
                      {d.notes ? <div className="text-xs" style={{ color: "var(--ink-2)" }}>{d.notes}</div> : null}
                    </td>
                    <td><span className="cd-chip">{d.category}</span></td>
                    <td className="font-mono text-xs">{d.issued_at || "—"}</td>
                    <td className="font-mono text-xs">
                      {d.expires_at || "—"}
                      {d.days_left !== null && d.days_left !== undefined && (
                        <div className="text-xs" style={{ color: d.status === "expired" ? "var(--red)" : d.status === "expiring" ? "var(--yellow)" : "var(--ink-2)" }}>
                          {d.days_left >= 0 ? `${d.days_left}d left` : `${Math.abs(d.days_left)}d ago`}
                        </div>
                      )}
                    </td>
                    <td>
                      {d.has_file ? (
                        <button onClick={() => download(d)} className="btn btn-ghost btn-sm" data-testid={`buyer-download-${d.document_id}`}>
                          <Download size={12} /> Download
                        </button>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Footer disclaimer */}
      <section className="container-cd pb-16 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        This dashboard refreshes in real time. Access expires {new Date(invite.expires_at).toLocaleDateString()}. {invite.view_count} views recorded.
      </section>
    </div>
  );
}

function Stat({ icon: Icon, value, label, color, last, small }) {
  return (
    <div className="p-6" style={{ background: "white", borderRight: last ? "none" : "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <Icon size={18} style={{ color: color || "var(--ink)" }} />
      <div className={small ? "font-display text-xl mt-3" : "font-display text-4xl mt-3"} style={{ color: color || "var(--ink)" }}>
        {value}
      </div>
      <div className="overline mt-2">{label}</div>
    </div>
  );
}

function Bucket({ title, items }) {
  return (
    <div className="cd-card p-6">
      <div className="overline">{title}</div>
      <div className="tag-list mt-4">
        {(items || []).length === 0 ? <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>—</span> : items.map((c, i) => <span key={i} className="cd-chip">{c}</span>)}
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    valid: { color: "var(--green)", label: "VALID" },
    expiring: { color: "var(--yellow)", label: "EXPIRING" },
    expired: { color: "var(--red)", label: "EXPIRED" },
  };
  const s = map[status] || { color: "var(--ink-2)", label: "—" };
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs">
      <span className="dot" style={{ background: s.color }} /> {s.label}
    </span>
  );
}
